import React, { useCallback, useRef } from 'react'
import useClickAway from 'react-use/lib/useClickAway'
import useToggle from 'react-use/lib/useToggle'

import { Button } from '../../atoms/Button'
import { mq, styled } from '../../../styled'

export type Option = {
  label: string
  value: string | number
}

type Props = {
  label?: string
  onChange?: (value: Option) => void
  options: Option[]
  value?: string | number
}

const DropdownWrapper = styled.div`
  position: relative;
  max-width: 55px;

  ${mq.md} {
    max-width: auto;
  }
`

const DropdownButton = styled(Button)`
  color: ${({ theme }) => theme.colors.white};
  min-width: 5em;

  ${mq.md} {
    min-width: 3em;
  }
`

const DropdownList = styled.ul`
  position: absolute;
  bottom: 100%;
  left: 0;
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.colorPallets.mono.black};
  color: ${({ theme }) => theme.colors.white};
  list-style-type: none;
`

const DropdownOptionButton = styled(Button)<{ isCurrent: boolean }>`
  padding: 0.5rem;
  color: ${({ isCurrent, theme }) =>
    isCurrent ? theme.colors.blue : theme.colors.white};
`

export const Dropdown: React.FC<Props> = ({
  label,
  onChange,
  options,
  value,
}) => {
  const ref = useRef<HTMLDivElement>(null)
  const [isOpen, toggleIsOpen] = useToggle(false)

  const onClick = useCallback<React.MouseEventHandler<HTMLButtonElement>>(
    () => toggleIsOpen(),
    [toggleIsOpen]
  )

  const onOptionClick = useCallback<NonNullable<Props['onChange']>>(
    option => {
      toggleIsOpen()
      return onChange && onChange(option)
    },
    [toggleIsOpen, onChange]
  )

  useClickAway(ref, () => toggleIsOpen(false))

  return (
    <DropdownWrapper ref={ref}>
      <DropdownButton bgColor="transparent" onClick={onClick}>
        {label || value}
      </DropdownButton>
      {isOpen && options.length !== 0 && (
        <DropdownList>
          {options.map(option => (
            <li key={option.value}>
              <DropdownOptionButton
                block
                bgColor="transparent"
                isCurrent={value === option.value}
                onClick={() => onOptionClick(option)}
              >
                {option.label}
              </DropdownOptionButton>
            </li>
          ))}
        </DropdownList>
      )}
    </DropdownWrapper>
  )
}
